import {
  Injectable,
  Inject,
  PLATFORM_ID,
  Injector,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UnifiedAuthService } from './unified-auth.service';
import type { PdvCaixaService } from './pdv-caixa.service';
import { UpdateModalComponent } from '../component/update-modal/update-modal.component';

type UpdateContext = 'site' | 'login' | 'authenticated' | 'pdv-busy';

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly DISMISS_KEY = 'update_dismissed_until';

  readonly updateAvailable = signal<boolean>(false);

  private updatePending = false;
  // Resolved lazily in browser only — PdvCaixaService deps (PdvKeyboardService) access document in constructor
  private pdvCaixaService: PdvCaixaService | null = null;
  private matDialog: MatDialog | null = null;

  constructor(
    private swUpdate: SwUpdate,
    private authService: UnifiedAuthService,
    private injector: Injector,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (!isPlatformBrowser(this.platformId) || !this.swUpdate.isEnabled) return;

    // Lazy-resolve MatDialog in browser (SSR-safe — CDK accesses document in constructor)
    this.matDialog = this.injector.get(MatDialog, null);

    // Only resolve on pdv.* subdomain — PdvCaixaService deps make API calls in constructors.
    // Import dinâmico: um import estático aqui arrastaria toda a cadeia de serviços do PDV
    // (~60 kB) para o bundle inicial de todas as páginas.
    if (window.location.hostname.includes('pdv.')) {
      import('./pdv-caixa.service').then(({ PdvCaixaService }) => {
        this.pdvCaixaService = this.injector.get(PdvCaixaService, null);

        // React passively when PDV clears an active order
        this.pdvCaixaService?.currentPedido$
          .pipe(filter((p) => p == null || p.idPedido == null))
          .subscribe(() => {
            if (this.updatePending) {
              this.updatePending = false;
              this.openModal();
            }
          });
      });
    }

    // Listen for new SW versions — only act for authenticated users
    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this.onVersionReady());
  }

  private onVersionReady(): void {
    switch (this.checkContext()) {
      case 'site':
      case 'login':
        // Handled by app.component.ts (original behavior: activate + reload)
        break;
      case 'pdv-busy':
        this.updatePending = true;
        this.updateAvailable.set(true);
        break;
      case 'authenticated':
        this.updateAvailable.set(true);
        this.openModal();
        break;
    }
  }

  private checkContext(): UpdateContext {
    if (typeof window === 'undefined') return 'site';
    const hostname = window.location.hostname;
    // Matches SiteDomainGuard logic in domain.guards.ts
    const isSite =
      !hostname.includes('app.') &&
      !hostname.includes('admin.') &&
      !hostname.includes('pdv.');
    if (isSite) return 'site';

    if (!this.authService.getUserInfos()) return 'login';

    const pedido = this.pdvCaixaService?.pedidoAtual;
    if (pedido?.idPedido != null) return 'pdv-busy';

    return 'authenticated';
  }

  private openModal(): void {
    const dismissedUntil = Number(localStorage.getItem(this.DISMISS_KEY) ?? 0);
    if (Date.now() < dismissedUntil) return;
    if (!this.matDialog || this.matDialog.openDialogs.length > 0) return;
    this.matDialog.open(UpdateModalComponent, {
      width: '400px',
      disableClose: true,
      panelClass: 'update-modal-panel',
    });
  }

  /** Called by the header button — re-opens the modal manually */
  showUpdateModal(): void {
    if (!this.matDialog || this.matDialog.openDialogs.length > 0) return;
    this.matDialog.open(UpdateModalComponent, {
      width: '400px',
      disableClose: true,
      panelClass: 'update-modal-panel',
    });
  }

  dismissUpdate(): void {
    const dismissedUntil = Date.now() + 30 * 60 * 1000;
    localStorage.setItem(this.DISMISS_KEY, String(dismissedUntil));
    setTimeout(() => {
      if (this.updateAvailable()) this.openModal();
    }, 30 * 60 * 1000);
  }

  async performUpdate(): Promise<void> {
    await this.swUpdate.activateUpdate();
    this.authService.logout();
    window.location.reload();
  }
}
